import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { pageContext, replaceBaseUrl, sanitizeInnerHtml } from 'ctm-util-functions';

const Container = styled.div`
    width: 102vw;
    color: ${(props) => props.fontColor};
    background: ${(props) => props.bgColor};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -2rem;
    margin-left: ${(props) => (props.containerMarginLeft != null ? props.containerMarginLeft : '-2rem')};
    padding-bottom: ${(props) => (props.containerPaddingBottom != null ? props.containerPaddingBottom : 'inherit')};
    padding-top: ${(props) => (props.containerPaddingTop != null ? props.containerPaddingTop : 'inherit')};
`;

const Flexbox = styled.div`
    display: flex;
    height: inherit;
    flex-direction: ${(props) => (props.layoutXs != null && props.isXs ? props.layoutXs : props.layout)};
    padding-top: ${(props) => (props.isXs ? props.topMarginMobile  : props.topMarginDesktop)};
    padding-bottom:  ${(props) => (props.isXs ? props.bottomMarginMobile : props.bottomMarginDesktop)};
    align-items: ${(props) => (((props.layout === 'row' || props.layout === 'row-reverse') && !props.isXs) ? 'space-between' : 'center')};
    justify-content: ${(props) => (((props.layout === 'row' || props.layout === 'row-reverse') && !props.isXs) ? 'space-between' : 'center')};
    max-width: 75rem;
    width: 100%;
    padding-left: ${(props) => (props.innerContainerPaddingLeft != null ? props.innerContainerPaddingLeft : '3.2rem')};
    padding-right: 3.2rem;
    img {
        object-fit: contain;
        margin-top: ${(props) => (props.isXs ? '1rem' : '0rem')};
        margin-bottom: ${(props) => (props.isXs ? '1rem' : '1rem')};
        height: ${(props) => (props.isXs ? '15rem' : '22rem')};
    }
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 0 2rem;
`;

const CustomHtmlBlock = styled.div`
    font-size: ${(props) => (props.isXs ? props.xsFontSize : props.fontSize)};
    font-weight: ${(props) => props.fontWeight};
    line-height: ${(props) => props.lineHeight};
    margin-left: ${(props) => props.marginLeft};
    margin-right: ${(props) => props.marginRight};
    br {
        display: ${(props) => (props.isXs ? 'none' : 'block')};
    }
`;

const Title = styled.h1`
    font-size: ${(props) => (props.isXs ? '2rem' : '3.2rem')};
    line-height: ${(props) => (props.isXs ? '2.7rem' : '4.3rem')};
`;

/**
 * Function for rendering a header banner with customizable properties.
 *
 * @param {object} props - the properties object containing layout, fontColor, backgroundColor, image, title, subtext, and html
 * @return {JSX.Element} the rendered header banner component
 */

const HeaderBanner = (props) => {
    const {
        properties: { layout, layoutXs, fontColor, backgroundColor, image, title, subtext, html, customHtml,
            bottomMarginDesktop, bottomMarginMobile, topMarginDesktop, topMarginMobile, containerPaddingBottom, containerPaddingTop, containerMarginLeft, innerContainerPaddingLeft, }
    } = props;
    const baseAssetsPath = pageContext.getProps().models.pageSchemaModel.getBaseAssetsPath();
    const isXs = pageContext.getProps().pageState.breakpoint === 'xs';

    const renderCustomHtml = (htmlProps) => {
        if (typeof window !== 'undefined') {
            const htmlTag = window.document.createElement('p');
            htmlTag.innerHTML = htmlProps.content;
            return (
                <CustomHtmlBlock
                    fontSize={htmlProps.attributes.fontSize}
                    xsFontSize={htmlProps.attributes.xsFontSize}
                    fontWeight={htmlProps.attributes.fontWeight}
                    lineHeight={htmlProps.attributes.lineHeight}
                    xsLineHeight={htmlProps.attributes.xsLineHeight}
                    isXs={isXs}
                    marginLeft={htmlProps.attributes.marginLeft}
                    marginRight={htmlProps.attributes.marginRight}
                    dangerouslySetInnerHTML={{ __html: sanitizeInnerHtml(htmlTag.innerHTML) }}
                />
            );
        }
        return null;
    };

    const renderTextBoxDefault = () => {
        return (
            <TextContainer>
                <Title isXs={isXs}>{title}</Title>
                {renderCustomHtml(html)}
            </TextContainer>
        );
    };

    return (
        <Container fontColor={fontColor} containerPaddingTop={containerPaddingTop} containerPaddingBottom={containerPaddingBottom} containerMarginLeft={containerMarginLeft} bgColor={backgroundColor} data-id={'header_banner'}>
            <Flexbox layout={layout}
                layoutXs={layoutXs}
                isXs={isXs}
                bottomMarginDesktop={bottomMarginDesktop}
                bottomMarginMobile={bottomMarginMobile}
                topMarginDesktop={topMarginDesktop}
                topMarginMobile={topMarginMobile}
                data-id="header-banner-flex-box"
                innerContainerPaddingLeft={innerContainerPaddingLeft}>
                <img src={replaceBaseUrl(image.src, baseAssetsPath)} alt={title} />
                {
                    customHtml ? customHtml() : renderTextBoxDefault()
                }
            </Flexbox>
        </Container>
    );
};

HeaderBanner.propTypes = {
    properties: PropTypes.shape({
        layout: PropTypes.string,
        layoutXs: PropTypes.string,
        containerPaddingBottom: PropTypes.string,
        containerPaddingTop: PropTypes.string,
        containerMarginLeft: PropTypes.string,
        innerContainerPaddingLeft: PropTypes.string,
        topMarginDesktop: PropTypes.string,
        topMarginMobile: PropTypes.string,
        bottomMarginDesktop: PropTypes.string,
        bottomMarginMobile: PropTypes.string,
        fontColor: PropTypes.string,
        backgroundColor: PropTypes.string,
        image: PropTypes.shape({
            src: PropTypes.string
        }),
        title: PropTypes.string,
        subtext: PropTypes.string,
        html: PropTypes.shape({
            content: PropTypes.string,
            attributes: PropTypes.shape({
                fontSize: PropTypes.string,
                fontWeight: PropTypes.string,
                lineHeight: PropTypes.string
            })
        }),
        customHtml: PropTypes.func
    })
};

export { HeaderBanner };
